import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { canPrompt, isInstalled, onPwaChange, promptInstall } from "@/lib/pwa";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, ChefHat, Download } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const [tab, setTab] = useState("login");
  const [busy, setBusy] = useState(false);
  const [lg, setLg] = useState({ identifier: "", password: "" });
  const [rg, setRg] = useState({ username: "", email: "", password: "", name: "", address: "", phone: "" });
  const [forgot, setForgot] = useState(null); // 'username' | 'password' | null
  const [fEmail, setFEmail] = useState("");
  const [fInfo, setFInfo] = useState("");
  const [installed, setInstalled] = useState(true);
  const navigate = useNavigate();
  const install = async () => {
    if (!canPrompt()) { navigate("/install"); return; }
    if (await promptInstall() === "accepted") toast.success("Aplikasi DA LE TANG sedang dipasang");
  };

  useEffect(() => {
    isInstalled().then(setInstalled);
    return onPwaChange(() => { isInstalled().then(setInstalled); });
  }, []);

  const doLogin = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try { await login(lg.identifier, lg.password); toast.success("Selamat datang! 🔥"); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await register(rg); toast.success("Pendaftaran berhasil! Selamat bermain."); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const submitForgot = async () => {
    setBusy(true); setFInfo("");
    try {
      const url = forgot === "username" ? "/auth/forgot-username" : "/auth/forgot-password";
      const { data } = await api.post(url, { email: fEmail });
      setFInfo(data.message + (data.reset_token ? ` (Token demo: ${data.reset_token})` : ""));
      toast.success("Email dikirim jika terdaftar.");
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-hotpot-grid p-4">
      <img src="https://images.unsplash.com/photo-1677030137853-03a83b0bd630?crop=entropy&cs=srgb&fm=jpg&q=85"
        alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-[0.12]" />
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="glass relative z-10 w-full max-w-md rounded-3xl p-6 sm:p-8 red-glow">
        <div className="mb-6 flex flex-col items-center text-center">
          <Brand size={68} showText={false} />
          <h1 className="mt-3 font-display text-2xl font-extrabold tracking-tight text-neutral-900">DA LE TANG HuoGuo</h1>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">大乐汤 · POS & Loyalty</p>
        </div>

        <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl bg-neutral-100 p-1">
          {["login", "register"].map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`rounded-lg py-2.5 text-sm font-bold transition-all ${
                tab === t ? "bg-crimson text-white shadow-lg" : "text-muted-foreground hover:text-white"}`}>
              {t === "login" ? "Masuk" : "Daftar Member"}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {tab === "login" ? (
            <motion.form key="login" onSubmit={doLogin} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 12 }} className="space-y-4">
              <div>
                <Label className="text-xs text-muted-foreground">Username atau Email</Label>
                <Input data-testid="login-identifier" value={lg.identifier} autoComplete="username"
                  onChange={(e) => setLg({ ...lg, identifier: e.target.value })}
                  className="touch-btn mt-1 bg-neutral-100 border-neutral-200 text-neutral-900" placeholder="username / email@..." required />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Password</Label>
                <Input data-testid="login-password" type="password" value={lg.password} autoComplete="current-password"
                  onChange={(e) => setLg({ ...lg, password: e.target.value })}
                  className="touch-btn mt-1 bg-neutral-100 border-neutral-200 text-neutral-900" placeholder="••••••••" required />
              </div>
              <div className="flex justify-between text-xs">
                <button type="button" data-testid="forgot-username-link" onClick={() => { setForgot("username"); setFInfo(""); }}
                  className="text-muted-foreground hover:text-gold">Lupa Username?</button>
                <button type="button" data-testid="forgot-password-link" onClick={() => { setForgot("password"); setFInfo(""); }}
                  className="text-muted-foreground hover:text-gold">Lupa Password?</button>
              </div>
              <Button data-testid="login-submit-button" disabled={busy}
                className="touch-btn w-full bg-crimson text-base font-bold hover:bg-red-500">
                {busy ? <Loader2 className="animate-spin" /> : "Masuk"}
              </Button>
            </motion.form>
          ) : (
            <motion.form key="register" onSubmit={doRegister} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }} className="space-y-3">
              {[
                ["name", "Nama Lengkap", "text", true], ["username", "Username", "text", true],
                ["email", "Email", "email", true], ["password", "Password", "password", true],
                ["address", "Alamat", "text", true], ["phone", "No HP", "tel", true],
              ].map(([k, ph, type, req]) => (
                <Input key={k} data-testid={`register-${k}`} type={type} placeholder={ph} required={req}
                  value={rg[k]} onChange={(e) => setRg({ ...rg, [k]: e.target.value })}
                  className="touch-btn bg-neutral-100 border-neutral-200 text-neutral-900" />
              ))}
              <Button data-testid="register-submit-button" disabled={busy}
                className="touch-btn w-full bg-gold text-base font-bold text-ebony hover:bg-amber-400">
                {busy ? <Loader2 className="animate-spin" /> : "Daftar & Main Sekarang"}
              </Button>
            </motion.form>
          )}
        </AnimatePresence>

        {!installed && (
          <button type="button" onClick={install} data-testid="install-app-btn"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl border border-crimson/30 py-2.5 text-sm font-bold text-crimson transition-colors hover:bg-crimson/10">
            <Download size={16} /> Instal Aplikasi
          </button>
        )}
      </motion.div>

      <Dialog open={!!forgot} onOpenChange={(o) => !o && setForgot(null)}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ChefHat className="text-crimson" size={18} />
              {forgot === "username" ? "Lupa Username" : "Lupa Password"}
            </DialogTitle>
            <DialogDescription>Masukkan email terdaftar. Kami akan mengirim {forgot === "username" ? "username" : "tautan reset"} Anda.</DialogDescription>
          </DialogHeader>
          <Input data-testid="forgot-email-input" type="email" placeholder="email@anda.com" value={fEmail}
            onChange={(e) => setFEmail(e.target.value)} className="touch-btn bg-neutral-100 border-neutral-200" />
          {fInfo && <p data-testid="forgot-info" className="rounded-lg bg-emerald-500/10 p-3 text-xs text-emerald-300">{fInfo}</p>}
          <Button data-testid="forgot-submit-btn" onClick={submitForgot} disabled={busy}
            className="touch-btn w-full bg-crimson font-bold hover:bg-red-500">
            {busy ? <Loader2 className="animate-spin" /> : "Kirim Email"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
